import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import Select from "react-select";
import Switch from "react-switch";
import { MapPin } from "lucide-react";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import AddressSidebarSales from "../../common-components/address-bar-sales";
import {
  autoPoStockPayAddressesGetAction,
  autoPoStockSettingsGetAction,
  autoPoStockSettingsSaveAction,
  autoPoStockShipAddressesGetAction,
  autoPoStockVendorsGetAction,
} from "../../../redux/actions/settings-action";

const VENDOR_PAGE_SIZE = 20;
// When a saved vendor isn't on the first page, keep paging to find its label,
// but stop after this many pages.
const MAX_VENDOR_PAGES_FOR_LOOKUP = 25;

const EMPTY_SETTINGS = {
  vaos_id: null,
  is_auto_stock: false,
  is_auto_po: false,
  vendor_id: null,
  vendor_pay_address_id: null,
  vendor_ship_address_id: null,
};

// ---- response helpers --------------------------------------------------------
// Pull the list out of a list response, whether `data` is the array itself or
// wraps it (data.vendors / data.addresses / data.data ...).
const extractList = (res, keys = []) => {
  const data = res?.data;
  if (Array.isArray(data)) return data;
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const vendorName = (v) =>
  v?.cmr_first_name || v?.cmr_vendor_name || v?.cmr_name || "";

const toVendorOption = (v) => ({
  value: v?.cmr_id,
  label: [v?.cmr_code, vendorName(v)].filter(Boolean).join(" - ") || v?.cmr_id,
});

// "pay" | "ship" -> the id / display fields, using the same field names as the
// app's existing address panel (cmr_pay_address_* / cmr_ship_address_*).
const addressId = (a, type) =>
  a?.[`cmr_${type}_address_id`] ?? a?.address_id ?? a?.id ?? null;

const addressLines = (a, type) =>
  ["street", "city", "district", "state", "pincode"]
    .map((part) => a?.[`cmr_${type}_address_${part}`])
    .filter(Boolean)
    .join(", ") ||
  a?.address ||
  "";

const promisify = (dispatch, action, data) =>
  new Promise((resolve, reject) =>
    dispatch(
      data === undefined ? action(resolve, reject) : action(data, resolve, reject)
    )
  );

// ---- settings card -----------------------------------------------------------
// Auto Replenishment card for the module settings page. It has its own APIs
// (not the template settings), so the page drives it through the ref:
// validate() / save() / reset().
const AutoReplenishmentSettings = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const savedRef = useRef(EMPTY_SETTINGS); // last saved state, for reset

  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorPage, setVendorPage] = useState(0);
  const [vendorHasNext, setVendorHasNext] = useState(true);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  const [payList, setPayList] = useState([]);
  const [shipList, setShipList] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const loadVendorPage = async (page) => {
    setVendorsLoading(true);
    try {
      const res = await promisify(dispatch, autoPoStockVendorsGetAction, {
        pageNumber: page,
        pageSize: VENDOR_PAGE_SIZE,
      });
      const list = extractList(res, ["vendors", "vendorList", "list"]).map(
        toVendorOption
      );
      const hasNext =
        res?.data?.pagination?.hasNextPage ?? list.length >= VENDOR_PAGE_SIZE;
      setVendorOptions((prev) => {
        const seen = new Set(prev.map((o) => o.value));
        return [...prev, ...list.filter((o) => o.value && !seen.has(o.value))];
      });
      setVendorPage(page);
      setVendorHasNext(hasNext);
      return { list, hasNext };
    } catch (err) {
      setVendorHasNext(false);
      return { list: [], hasNext: false };
    } finally {
      setVendorsLoading(false);
    }
  };

  const loadAddresses = async (cmr_id) => {
    setPayList([]);
    setShipList([]);
    if (!cmr_id) return;
    setAddressesLoading(true);
    const [pay, ship] = await Promise.allSettled([
      promisify(dispatch, autoPoStockPayAddressesGetAction, { cmr_id }),
      promisify(dispatch, autoPoStockShipAddressesGetAction, { cmr_id }),
    ]);
    setPayList(
      pay.status === "fulfilled"
        ? extractList(pay.value, ["addressPay", "addresses", "payAddresses"])
        : []
    );
    setShipList(
      ship.status === "fulfilled"
        ? extractList(ship.value, [
            "addressShipping",
            "addresses",
            "shipAddresses",
          ])
        : []
    );
    setAddressesLoading(false);
  };

  // Initial load: saved settings, then the first vendor page (paging on until
  // the saved vendor's label is found), then that vendor's addresses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let saved = EMPTY_SETTINGS;
      try {
        const res = await promisify(dispatch, autoPoStockSettingsGetAction);
        // "Settings not configured" comes back as code 200 with data: null.
        if (res?.data) saved = { ...EMPTY_SETTINGS, ...res.data };
        // Only one mode can be active. Settings saved before that rule may have
        // both on - keep Auto PO (it carries the vendor setup).
        if (saved.is_auto_po && saved.is_auto_stock) {
          saved = { ...saved, is_auto_stock: false };
        }
      } catch (err) {
        errorToast(
          err?.message ||
            err?.data?.message ||
            "Failed to load auto replenishment settings"
        );
      }
      if (cancelled) return;
      savedRef.current = saved;
      setSettings(saved);
      setLoading(false);

      let page = 1;
      let { list, hasNext } = await loadVendorPage(page);
      let found = !saved.vendor_id || list.some((o) => o.value === saved.vendor_id);
      while (!cancelled && !found && hasNext && page < MAX_VENDOR_PAGES_FOR_LOOKUP) {
        page += 1;
        ({ list, hasNext } = await loadVendorPage(page));
        found = list.some((o) => o.value === saved.vendor_id);
      }
      if (!cancelled && saved.vendor_id) loadAddresses(saved.vendor_id);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (patch) => setSettings((prev) => ({ ...prev, ...patch }));

  // Feed the app's shared address panel in its external mode.
  const addressSource = useMemo(
    () => ({
      pay: payList,
      ship: shipList,
      payId: settings.vendor_pay_address_id,
      shipId: settings.vendor_ship_address_id,
      loading: addressesLoading,
    }),
    [
      payList,
      shipList,
      settings.vendor_pay_address_id,
      settings.vendor_ship_address_id,
      addressesLoading,
    ]
  );

  const onVendorChange = (option) => {
    const vendor_id = option?.value ?? null;
    // A new vendor has its own addresses, so drop the old selection.
    update({
      vendor_id,
      vendor_pay_address_id: null,
      vendor_ship_address_id: null,
    });
    loadAddresses(vendor_id);
    if (vendor_id) setPanelOpen(true);
  };

  const selectedVendor =
    vendorOptions.find((o) => o.value === settings.vendor_id) ||
    (settings.vendor_id
      ? { value: settings.vendor_id, label: "Selected vendor" }
      : null);
  const selectedPay = payList.find(
    (a) => addressId(a, "pay") === settings.vendor_pay_address_id
  );
  const selectedShip = shipList.find(
    (a) => addressId(a, "ship") === settings.vendor_ship_address_id
  );

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (!settings.is_auto_po) return true;
      if (!settings.vendor_id) {
        errorToast("Select a vendor for Auto PO");
        return false;
      }
      if (!settings.vendor_pay_address_id || !settings.vendor_ship_address_id) {
        errorToast("Select the vendor's pay to and ship to address for Auto PO");
        return false;
      }
      return true;
    },
    save: async () => {
      const payload = {
        vaos_id: settings.vaos_id ?? null,
        is_auto_stock: !!settings.is_auto_stock && !settings.is_auto_po,
        is_auto_po: !!settings.is_auto_po,
        // The vendor only applies to Auto PO; clear it when Auto PO is off.
        vendor_id: settings.is_auto_po ? settings.vendor_id : null,
        vendor_ship_address_id: settings.is_auto_po
          ? settings.vendor_ship_address_id
          : null,
        vendor_pay_address_id: settings.is_auto_po
          ? settings.vendor_pay_address_id
          : null,
      };
      try {
        const res = await promisify(
          dispatch,
          autoPoStockSettingsSaveAction,
          payload
        );
        const saved = {
          ...EMPTY_SETTINGS,
          ...payload,
          ...(res?.data && typeof res.data === "object" ? res.data : {}),
        };
        savedRef.current = saved;
        setSettings(saved);
        successToast(res?.message || "Auto replenishment settings saved");
      } catch (err) {
        errorToast(
          err?.message ||
            err?.data?.message ||
            "Failed to save auto replenishment settings"
        );
        throw err;
      }
    },
    reset: () => {
      const saved = savedRef.current;
      setSettings(saved);
      loadAddresses(saved.vendor_id);
    },
  }));

  const toggleRow = (label, description, key) => (
    <div className="flex items-center justify-between py-5 border-b border-gray-200 last:border-b-0">
      <div className="flex flex-col">
        <span className="font-medium text-base text-gray-900">{label}</span>
        <span className="text-xs text-gray-500 mt-1">{description}</span>
      </div>
      <Switch
        checked={!!settings[key]}
        onChange={(checked) =>
          // Auto Stock and Auto PO are mutually exclusive: turning one on turns
          // the other off. Both can be off.
          update(
            checked
              ? {
                  is_auto_stock: key === "is_auto_stock",
                  is_auto_po: key === "is_auto_po",
                }
              : { [key]: false }
          )
        }
        onColor="#3b82f6"
        offColor="#d2d5da"
        handleDiameter={20}
        uncheckedIcon={false}
        checkedIcon={false}
        height={24}
        width={40}
      />
    </div>
  );

  return (
    <div className="mb-8 bg-white border border-gray-200 rounded-xl px-7 py-6">
      <div className="text-2xl font-bold text-gray-900 mb-2">
        Auto Replenishment
      </div>
      {loading ? (
        <div className="text-sm text-gray-500 py-4">Loading...</div>
      ) : (
        <>
          {toggleRow(
            "Auto Stock",
            "Automatically replenish stock",
            "is_auto_stock"
          )}
          {toggleRow(
            "Auto PO",
            "Automatically raise purchase orders to the selected vendor",
            "is_auto_po"
          )}

          {settings.is_auto_po && (
            <div className="py-5 flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <label className="font-medium text-base text-gray-900 min-w-[6rem]">
                  Vendor <span className="text-red-500">*</span>
                </label>
                <div className="flex-1">
                  <Select
                    classNamePrefix="auto-po-vendor"
                    placeholder="Select vendor"
                    options={vendorOptions}
                    value={selectedVendor}
                    onChange={onVendorChange}
                    isLoading={vendorsLoading}
                    isSearchable
                    onMenuScrollToBottom={() => {
                      if (vendorHasNext && !vendorsLoading) {
                        loadVendorPage(vendorPage + 1);
                      }
                    }}
                    menuPortalTarget={document.body}
                    styles={{ menuPortal: (base) => ({ ...base, zIndex: 60 }) }}
                  />
                </div>
                <button
                  type="button"
                  disabled={!settings.vendor_id}
                  onClick={() => setPanelOpen(true)}
                  className="flex items-center gap-2 px-4 h-[38px] rounded-md border border-gray-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:border-primary"
                >
                  <MapPin width={16} height={16} />
                  Address
                </button>
              </div>

              {settings.vendor_id && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="border border-gray-200 rounded-md px-3 py-2">
                    <div className="text-xs text-gray-500 mb-1">Pay To</div>
                    <div className="text-gray-900">
                      {selectedPay
                        ? addressLines(selectedPay, "pay") || "Selected"
                        : settings.vendor_pay_address_id
                        ? "Selected"
                        : "Not selected"}
                    </div>
                  </div>
                  <div className="border border-gray-200 rounded-md px-3 py-2">
                    <div className="text-xs text-gray-500 mb-1">Ship To</div>
                    <div className="text-gray-900">
                      {selectedShip
                        ? addressLines(selectedShip, "ship") || "Selected"
                        : settings.vendor_ship_address_id
                        ? "Selected"
                        : "Not selected"}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <AddressSidebarSales
        open={panelOpen}
        setOpen={setPanelOpen}
        addressSource={addressSource}
        onSelectAddress={(pay, ship) =>
          update({
            vendor_pay_address_id: addressId(pay, "pay"),
            vendor_ship_address_id: addressId(ship, "ship"),
          })
        }
      />
    </div>
  );
});

export default AutoReplenishmentSettings;
